import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  clipFactorsFromLaunch,
  resolveLaunchClipMs,
} from "../../components/performanceChartV3Utils";
import {
  actualItemsToFactors,
  useActualPerformance,
} from "../../hooks/useActualPerformance";
import investorStrategyService from "../../services/investorStrategyService";
import type { FactorEntry } from "../../services/productFactorsService";
import { isValidPlutusUserId } from "../../utils/plutusUserId";
import { hasDuplicateAlphaPerformanceDates } from "./alphaPerformanceMath";
import {
  useAlphaStrategyVsActualComparison,
  type AlphaStrategyVsActualComparisonResult,
} from "./useAlphaStrategyVsActualComparison";
import type { AlphaStrategyActualPerformanceResult } from "./useAlphaStrategyActualPerformance";

export interface UseAdminCustomerViewStrategyPerformanceParams {
  targetUserId: string;
  enabled: boolean;
  includeActual: boolean;
  launchDate?: string | null;
  selectedKey: string;
}

export const adminCustomerViewStrategyFactorQueryKey = (targetUserId: string) =>
  ["alpha", "admin-customer-view", "strategy-factors", targetUserId] as const;

async function fetchAllStrategyFactorsForUser(targetUserId: string): Promise<FactorEntry[]> {
  const pageSize = 10000;
  let page = 1;
  const items: FactorEntry[] = [];
  while (true) {
    const response = await investorStrategyService.getStrategyFactorsPageForUser(targetUserId, {
      page,
      pageSize,
    });
    items.push(...response.items);
    if (response.currentPage >= response.totalPages || response.totalPages === 0) {
      break;
    }
    page += 1;
  }
  return items;
}

export function useAdminCustomerViewStrategyPerformance(
  params: UseAdminCustomerViewStrategyPerformanceParams
): AlphaStrategyActualPerformanceResult {
  const enabled = params.enabled === true && isValidPlutusUserId(params.targetUserId);
  const factorQuery = useQuery<FactorEntry[], Error>({
    queryKey: adminCustomerViewStrategyFactorQueryKey(params.targetUserId),
    queryFn: () => fetchAllStrategyFactorsForUser(params.targetUserId),
    enabled,
    staleTime: 15 * 60 * 1000,
    retry: false,
    retryOnMount: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const strategyFactors = useMemo(() => {
    const launchClipMs = resolveLaunchClipMs(params.launchDate);
    return clipFactorsFromLaunch(factorQuery.data ?? [], launchClipMs);
  }, [factorQuery.data, params.launchDate]);

  const {
    data: actualData,
    isLoading: actualLoading,
    isFetching: actualFetching,
    error: actualQueryError,
    refetch: refetchActual,
  } = useActualPerformance(
    params.targetUserId,
    enabled && params.includeActual
  );

  const launchClipMs = useMemo(
    () => resolveLaunchClipMs(params.launchDate),
    [params.launchDate]
  );
  const clippedActualFactors = useMemo(
    () => params.includeActual
      ? clipFactorsFromLaunch(
        actualData?.items ? actualItemsToFactors(actualData.items) : [],
        launchClipMs
      )
      : [],
    [actualData, params.includeActual, launchClipMs]
  );
  const actualHasDuplicateDates = useMemo(
    () => hasDuplicateAlphaPerformanceDates(clippedActualFactors),
    [clippedActualFactors]
  );
  const actualFactors = useMemo(
    () => actualHasDuplicateDates ? [] : clippedActualFactors,
    [actualHasDuplicateDates, clippedActualFactors]
  );

  const comparisonStrategyFactors = params.includeActual && actualLoading
    ? []
    : strategyFactors;

  const comparison = useAlphaStrategyVsActualComparison({
    isActive: enabled,
    selectedKey: params.selectedKey,
    strategyFactors: comparisonStrategyFactors,
    actualFactors,
    clockKey: params.targetUserId,
  });

  const retryActual = useCallback(() => {
    if (!params.includeActual) return;
    void refetchActual();
  }, [params.includeActual, refetchActual]);

  const retry = useCallback(() => {
    void factorQuery.refetch();
    retryActual();
  }, [factorQuery, retryActual]);

  const chart = useMemo<AlphaStrategyVsActualComparisonResult>(() => ({
    ...comparison,
    loading: factorQuery.isLoading || (params.includeActual && actualLoading),
    error: factorQuery.error ? "Strategy performance could not be loaded." : null,
  }), [actualLoading, comparison, factorQuery.error, factorQuery.isLoading, params.includeActual]);

  return {
    chart,
    strategyFactors,
    alignedStrategyFactors: comparison.strategyFactorsFromSharedStart,
    actualFactors,
    actualError: params.includeActual && actualQueryError
      ? "Actual performance could not be loaded."
      : params.includeActual && actualHasDuplicateDates
        ? "Actual performance could not be calculated safely."
        : null,
    isActualFetching: params.includeActual && actualFetching,
    retry,
    retryActual,
  };
}
